import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';

import {
  selectSurvey,
  selectManageSurveyLoading,
  selectManageSurveyError,
  selectChildSurvey,
  selectChildSurveyLoading,
  selectChildSurveyError,
  selectHealthMetrics,
  selectHealthMetricsLoading,
  selectHealthMetricsError,
  selectRemainingHours,
  selectRemainingHoursLoading,
  selectRemainingHoursError,
  selectReconciliationAttempts,
  selectReconciliationAttemptsLoading,
  selectReconciliationAttemptsError,
  selectActivityLogs,
  selectActivityLogsLoading,
  selectActivityLogsError,
  selectComments,
  selectCommentsLoading,
  selectCommentsError,
  selectAddingComment,
  selectAddCommentError,
  selectQuotas,
  selectQuotasLoading,
  selectQuotaTotal,
  selectQuotaSearch,
  selectQuotasLoadingMore,
  selectQuotaLockingIds,
  selectQuotaSavingIds,
  selectQuotaCurrentlyOpenSavingIds,
  selectSupplierQuotaSavingIds,
  selectSupplierQuotaCurrentlyOpenSavingIds,
  selectQuotaRenamingIds,
  selectQuotaCpiSavingIds,
  selectTrafficChannels,
  selectTrafficChannelsLoading,
  selectTrafficChannelsError,
  selectTcStatusChangingIds,
  selectTcCpiSavingIds,
  selectCreatingTrafficChannel,
  selectTrafficChannelCreationError,
  selectPsidUploadLoading,
  selectPsidUploadError,
  selectPsidUploadFilePath,
  selectPsidUploadUniqCount,
  selectPsidUploadTotalCount,
  selectPsidUploadUniqPsids,
  selectCrmIdSaving,
  selectCrmIdError,
  selectAsanaIdSaving,
  selectAsanaIdError,
  selectBillingNumberSaving,
  selectBillingNumberError,
  selectCustomRedirectFieldSaving,
  selectCustomRedirectFieldError,
} from './manage-survey.selectors';
import { selectMentionableUsers } from '../mentionable-users/mentionable-users.selectors';

@Injectable({ providedIn: 'root' })
export class ManageSurveySelectorsFacade {
  private readonly _store = inject(Store);

  public readonly survey = this._store.selectSignal(selectSurvey);

  public readonly childSurvey = this._store.selectSignal(selectChildSurvey);

  public readonly healthMetrics = this._store.selectSignal(selectHealthMetrics);

  public readonly healthMetricsLoading = this._store.selectSignal(
    selectHealthMetricsLoading,
  );

  public readonly healthMetricsError = this._store.selectSignal(
    selectHealthMetricsError,
  );

  public readonly remainingHours =
    this._store.selectSignal(selectRemainingHours);

  public readonly remainingHoursLoading = this._store.selectSignal(
    selectRemainingHoursLoading,
  );

  public readonly remainingHoursError = this._store.selectSignal(
    selectRemainingHoursError,
  );

  public readonly reconciliationAttempts = this._store.selectSignal(
    selectReconciliationAttempts,
  );

  public readonly reconciliationAttemptsLoading = this._store.selectSignal(
    selectReconciliationAttemptsLoading,
  );

  public readonly reconciliationAttemptsError = this._store.selectSignal(
    selectReconciliationAttemptsError,
  );

  public readonly activityLogs = this._store.selectSignal(selectActivityLogs);

  public readonly activityLogsLoading = this._store.selectSignal(
    selectActivityLogsLoading,
  );

  public readonly activityLogsError = this._store.selectSignal(
    selectActivityLogsError,
  );

  public readonly comments = this._store.selectSignal(selectComments);

  public readonly commentsLoading = this._store.selectSignal(
    selectCommentsLoading,
  );

  public readonly commentsError = this._store.selectSignal(selectCommentsError);

  public readonly addingComment = this._store.selectSignal(selectAddingComment);

  public readonly addCommentError = this._store.selectSignal(
    selectAddCommentError,
  );

  public readonly childSurveyLoading = this._store.selectSignal(
    selectChildSurveyLoading,
  );

  public readonly childSurveyError = this._store.selectSignal(
    selectChildSurveyError,
  );

  public readonly manageSurveyLoading = this._store.selectSignal(
    selectManageSurveyLoading,
  );

  public readonly manageSurveyError = this._store.selectSignal(
    selectManageSurveyError,
  );

  public readonly quotas = this._store.selectSignal(selectQuotas);

  public readonly quotasLoading = this._store.selectSignal(selectQuotasLoading);

  public readonly quotaTotal = this._store.selectSignal(selectQuotaTotal);

  public readonly quotaSearch = this._store.selectSignal(selectQuotaSearch);

  public readonly quotasLoadingMore = this._store.selectSignal(
    selectQuotasLoadingMore,
  );

  public readonly quotaLockingIds = this._store.selectSignal(
    selectQuotaLockingIds,
  );

  public readonly quotaSavingIds =
    this._store.selectSignal(selectQuotaSavingIds);

  public readonly quotaCurrentlyOpenSavingIds = this._store.selectSignal(
    selectQuotaCurrentlyOpenSavingIds,
  );

  public readonly supplierQuotaSavingIds = this._store.selectSignal(
    selectSupplierQuotaSavingIds,
  );

  public readonly supplierQuotaCurrentlyOpenSavingIds =
    this._store.selectSignal(selectSupplierQuotaCurrentlyOpenSavingIds);

  public readonly quotaRenamingIds = this._store.selectSignal(
    selectQuotaRenamingIds,
  );

  public readonly quotaCpiSavingIds = this._store.selectSignal(
    selectQuotaCpiSavingIds,
  );

  public readonly trafficChannels = this._store.selectSignal(
    selectTrafficChannels,
  );

  public readonly trafficChannelsLoading = this._store.selectSignal(
    selectTrafficChannelsLoading,
  );

  public readonly trafficChannelsError = this._store.selectSignal(
    selectTrafficChannelsError,
  );

  public readonly tcStatusChangingIds = this._store.selectSignal(
    selectTcStatusChangingIds,
  );

  public readonly tcCpiSavingIds =
    this._store.selectSignal(selectTcCpiSavingIds);

  public readonly creatingTrafficChannel = this._store.selectSignal(
    selectCreatingTrafficChannel,
  );

  public readonly trafficChannelCreationError = this._store.selectSignal(
    selectTrafficChannelCreationError,
  );

  public readonly psidUploadLoading = this._store.selectSignal(
    selectPsidUploadLoading,
  );

  public readonly psidUploadError = this._store.selectSignal(
    selectPsidUploadError,
  );

  public readonly psidUploadFilePath = this._store.selectSignal(
    selectPsidUploadFilePath,
  );

  public readonly psidUploadUniqCount = this._store.selectSignal(
    selectPsidUploadUniqCount,
  );

  public readonly psidUploadTotalCount = this._store.selectSignal(
    selectPsidUploadTotalCount,
  );

  public readonly psidUploadUniqPsids = this._store.selectSignal(
    selectPsidUploadUniqPsids,
  );

  public readonly crmIdSaving = this._store.selectSignal(selectCrmIdSaving);

  public readonly crmIdError = this._store.selectSignal(selectCrmIdError);

  public readonly asanaIdSaving = this._store.selectSignal(selectAsanaIdSaving);

  public readonly asanaIdError = this._store.selectSignal(selectAsanaIdError);

  public readonly billingNumberSaving = this._store.selectSignal(
    selectBillingNumberSaving,
  );

  public readonly billingNumberError = this._store.selectSignal(
    selectBillingNumberError,
  );

  public readonly customRedirectFieldSaving = this._store.selectSignal(
    selectCustomRedirectFieldSaving,
  );

  public readonly customRedirectFieldError = this._store.selectSignal(
    selectCustomRedirectFieldError,
  );

  public readonly mentionableUsers = this._store.selectSignal(
    selectMentionableUsers,
  );
}
